import L from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { Popup, useMap } from 'react-leaflet';
import type { BuoyDevice } from 'types/devices.types';
import DeviceMapPopup from '../../DeviceMap/components/popup/DeviceMapPopup';
import BuoyMapLayerIcon from './BuoyMapLayerIcon';

interface BuoyMapLayerProps {
  showAllBuoyDevices: boolean;
  buoyPoints?: BuoyDevice[] | null;
  setUseThisDevice?: (device: BuoyDevice) => void;
  setMapCenter?: (coords: [number, number]) => void;
  setMapZoom?: (zoom: number) => void;
  setShowCurrentDevice?: (value: boolean) => void;
  setShowDeviceSettings?: (value: boolean) => void;
  clusteringEnabled?: boolean;
}

/**
 * Performance-optimized BuoyMapLayer
 * - Uses a single Leaflet GeoJSON layer with canvas-backed CircleMarkers
 *   instead of thousands of individual React Marker components.
 * - Clicks are handled on the Leaflet layer and routed to a single
 *   React Popup so we only ever render one popup component.
 */
export default function BuoyMapLayer({
  showAllBuoyDevices,
  buoyPoints,
  setUseThisDevice,
  setMapCenter,
  setMapZoom,
  setShowCurrentDevice,
  setShowDeviceSettings,
  clusteringEnabled = true,
}: BuoyMapLayerProps): JSX.Element | null {
  const map = useMap();
  // Safety: If map is not available, render nothing
  if (!map) return null;
  const geoLayerRef = useRef<L.GeoJSON | null>(null);
  const superIndexRef = useRef<any | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<BuoyDevice | null>(null);
  const [popupLatLng, setPopupLatLng] = useState<[number, number] | null>(null);

  // --- Virtualization: Only render points in current map bounds ---
  useEffect(() => {
    if (!showAllBuoyDevices || !Array.isArray(buoyPoints) || !map) {
      if (geoLayerRef.current) {
        map.removeLayer(geoLayerRef.current);
        geoLayerRef.current = null;
      }
      // Only update state if needed to avoid infinite loop
      if (selectedPoint !== null) setSelectedPoint(null);
      if (popupLatLng !== null) setPopupLatLng(null);
      return;
    }

    // Helper to convert BuoyDevice[] to GeoJSON
    const toGeoJSON = (points: BuoyDevice[]) => ({
      type: 'FeatureCollection',
      features: points.map((p) => ({
        type: 'Feature',
        properties: p,
        geometry: {
          type: 'Point',
          coordinates: [p.location.lng, p.location.lat],
        },
      })),
    });

    // Cluster points that are within a certain pixel distance
    function clusterPoints(points: BuoyDevice[], map: L.Map, pxDist = 40) {
      const clusters: any[] = [];
      const assigned = new Array(points.length).fill(false);
      for (let i = 0; i < points.length; ++i) {
        if (assigned[i]) continue;
        const p1 = points[i];
        const ll1 = L.latLng(p1.location.lat, p1.location.lng);
        const p1Px = map.project(ll1, map.getZoom());
        const cluster = [p1];
        assigned[i] = true;
        for (let j = i + 1; j < points.length; ++j) {
          if (assigned[j]) continue;
          const p2 = points[j];
          const ll2 = L.latLng(p2.location.lat, p2.location.lng);
          const p2Px = map.project(ll2, map.getZoom());
          const dist = p1Px.distanceTo(p2Px);
          if (dist < pxDist) {
            cluster.push(p2);
            assigned[j] = true;
          }
        }
        clusters.push(cluster);
      }
      return clusters;
    }

    // Create a DivIcon for a cluster or single point using the React component
    function createDivIcon(cluster: BuoyDevice[]) {
      // Render the React component to static markup
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const ReactDOMServer = require('react-dom/server');
      const html = ReactDOMServer.renderToStaticMarkup(<BuoyMapLayerIcon cluster={cluster} />);
      return L.divIcon({
        className: 'divIconEmpty',
        html,
        iconSize: [90, 60 + (cluster.length > 1 ? 16 : 0)],
        iconAnchor: [45, 30],
      });
    }

    // Helper to attach click/hover handlers
    const attachFeatureHandlers = (
      onClick: (props: BuoyDevice, latlng: L.LatLng) => void,
      onHover?: {
        onMouseOver?: (layer: L.Layer) => void;
        onMouseOut?: (layer: L.Layer) => void;
      },
    ) => {
      return (feature: any, layer: L.Layer) => {
        layer.on('click', (e: any) => {
          onClick(feature.properties as BuoyDevice, e.latlng as L.LatLng);
        });
        if (onHover?.onMouseOver) layer.on('mouseover', () => onHover.onMouseOver!(layer));
        if (onHover?.onMouseOut) layer.on('mouseout', () => onHover.onMouseOut!(layer));
      };
    };

    // --- Virtualization logic ---
    const renderLayer = () => {
      if (geoLayerRef.current) {
        map.removeLayer(geoLayerRef.current);
        geoLayerRef.current = null;
      }
      if (!map || !Array.isArray(buoyPoints)) return;
      // Only show points in current map bounds
      let bounds;
      try {
        bounds = map.getBounds();
      } catch (e) {
        return;
      }
      const visiblePoints = buoyPoints.filter((p) => {
        if (
          !p ||
          !p.location ||
          typeof p.location.lat !== 'number' ||
          typeof p.location.lng !== 'number'
        )
          return false;
        return bounds.contains([p.location.lat, p.location.lng]);
      });
      if (!visiblePoints.length) return;

      // Cluster points that are close together (in pixel space)
      const clusters = clusterPoints(visiblePoints, map, 125); // px distance for clustering (more aggressive)

      // For each cluster, create a GeoJSON feature at the average location
      const features = clusters.map((cluster) => {
        // Average lat/lng
        let latSum = 0,
          lngSum = 0;
        for (const p of cluster) {
          latSum += p.location.lat;
          lngSum += p.location.lng;
        }
        const lat = latSum / cluster.length;
        const lng = lngSum / cluster.length;
        return {
          type: 'Feature',
          properties: { cluster },
          geometry: { type: 'Point', coordinates: [lng, lat] },
        };
      });
      const geojson = { type: 'FeatureCollection', features };

      // Use DivIcons for each cluster/point
      const pointToLayer = (feature: any, latlng: L.LatLng) => {
        const cluster = feature.properties.cluster;
        return L.marker(latlng, { icon: createDivIcon(cluster) });
      };

      const onEachFeature = (feature: any, layer: L.Layer) => {
        layer.on('click', (e: any) => {
          const cluster = feature.properties.cluster;
          if (cluster && cluster.length > 1) {
            // if clustered, zoom in on the cluster to separate the points
            const currentZoom = map.getZoom();
            const maxZoom = map.getMaxZoom ? map.getMaxZoom() : 18;
            // Increase zoom by 2, but not beyond maxZoom
            const newZoom = Math.min(currentZoom + 2, maxZoom);
            map.setView(e.latlng, newZoom, { animate: true });
          } else if (cluster && cluster.length === 1) {
            setSelectedPoint(cluster[0]);
            setPopupLatLng([e.latlng.lat, e.latlng.lng]);
          }
        });
      };

      try {
        const layer = L.geoJSON(geojson as any, { pointToLayer, onEachFeature });
        geoLayerRef.current = layer;
        geoLayerRef.current.addTo(map);
      } catch (e) {
        // Fail silently if Leaflet throws
      }
    };

    renderLayer();
    map.on('moveend zoomend', renderLayer);

    return () => {
      map.off('moveend zoomend', renderLayer);
      if (geoLayerRef.current) {
        map.removeLayer(geoLayerRef.current);
        geoLayerRef.current = null;
      }
    };
  }, [map, showAllBuoyDevices, buoyPoints, clusteringEnabled]);

  const closePopup = () => {
    setSelectedPoint(null);
    setPopupLatLng(null);
  };

  useEffect(() => {
    const handler = (e: any) => {
      const latlng = e?.popup?.getLatLng?.();
      if (!latlng) return;
      if (popupLatLng && latlng.lat === popupLatLng[0] && latlng.lng === popupLatLng[1]) {
        closePopup();
      }
    };
    map.on('popupclose', handler);
    return () => {
      map.off('popupclose', handler);
    };
  }, [map, popupLatLng]);

  if (!showAllBuoyDevices || !map) return null;

  return (
    <>
      {popupLatLng &&
        selectedPoint &&
        selectedPoint.location &&
        typeof selectedPoint.location.lat === 'number' &&
        typeof selectedPoint.location.lng === 'number' && (
          <Popup position={popupLatLng} className="base-map-popup-leaflet" closeButton={false}>
            <DeviceMapPopup
              device={selectedPoint}
              onClose={() => {
                map.closePopup();
                closePopup();
              }}
              onViewDevice={() => {
                if (map && setMapCenter && setMapZoom && selectedPoint.location) {
                  map.closePopup();
                  setMapCenter([selectedPoint.location.lat, selectedPoint.location.lng]);
                  setMapZoom(Math.max(map.getZoom(), 11));
                  closePopup();
                }
              }}
            />
          </Popup>
        )}
    </>
  );
}
